/**
 * Route guards.
 *
 * A guard decides whether a ROUTE may be entered. It is a UX control, not a
 * security control: it prevents a user from reaching a screen that would only
 * show them an error, and it keeps navigation honest. The backend authorizes
 * every request regardless of what these return.
 *
 * The distinction matters because the failure modes differ. A guard that is too
 * strict sends a legitimate user to a page they should see. A guard that is too
 * loose shows a screen whose every action fails — annoying, but not a breach,
 * because nothing here grants server access.
 */
import { redirect } from '@tanstack/react-router';
import { bootstrapSession, getSessionSnapshot, safeRedirectPath } from '@/app/session';
import { checkPermissions } from '@/shared/permissions';
import { getWorkspaceResolution } from '@/app/workspace/store';
import type { PermissionSpec } from '@/shared/permissions';
import type { SessionSnapshot } from '@/app/session/types';

export type GuardContext = {
  location: { href: string };
};

/**
 * Requires an authenticated session.
 *
 * The deep link survives the auth detour as a VALIDATED `?redirect=`. It is
 * validated on the way back, not on the way out, because the outbound value
 * comes from our own router and the return value does not.
 */
export async function requireSession({ location }: GuardContext): Promise<void> {
  const session = await bootstrapSession();

  if (session.status !== 'authenticated') {
    throw redirect({
      to: '/login',
      search: { redirect: safeRedirectPath(location.href, '/overview') },
    });
  }
}

/**
 * Requires an authenticated session AND a resolved workspace.
 *
 * Separate from `requireSession` because a signed-in user on an unrecognised or
 * suspended host is a real state, and it must NOT be routed to `/denied` — that
 * would tell a legitimate operator their permissions are wrong when the truth is
 * that the address is not recognised. It goes to a dedicated unresolved route.
 */
export async function requireWorkspace({ location }: GuardContext): Promise<void> {
  await requireSession({ location });

  const resolution = getWorkspaceResolution();
  if (resolution.status !== 'resolved') {
    throw redirect({ to: '/workspace-unresolved' });
  }
}

/**
 * Requires the given effective permissions.
 *
 * Union-of-roles, resolved server-side. On failure the user lands on `/denied`
 * rather than a partially-populated screen: a page whose every action fails is
 * more confusing, and more likely to be screenshotted into a bug report, than
 * an honest "you cannot do this here".
 */
export function requirePermission(spec: PermissionSpec) {
  return async ({ location }: GuardContext): Promise<void> => {
    await requireSession({ location });

    const { user } = getSessionSnapshot();
    if (user && !checkPermissions(user.permissions, spec)) {
      throw redirect({ to: '/denied' });
    }
  };
}

/**
 * Public-only routes: a signed-in user skips the auth screens entirely.
 *
 * Uses the quiet bootstrap, so visiting `/login` while authenticated cannot
 * trigger the silent-refresh recovery path and bounce the user back out.
 *
 * ---------------------------------------------------------------------------
 * A FAILED PROBE SHOWS THE LOGIN FORM
 * ---------------------------------------------------------------------------
 * This guard asks one question: "is there already a session to redirect away from?"
 * When the probe FAILS, the answer is "we do not know" — and the right response to not
 * knowing is to SHOW THE LOGIN FORM, not to replace it with an error boundary.
 *
 * That is not a corner case. The provider throttles sign-in, and a throttled session
 * probe answers 429; the deployed API once answered that same refusal with 500. Either
 * way the probe fails, and a visitor who is throttled must still be able to reach the
 * form and try — the attempt itself reports the refusal properly, with its own message.
 *
 * Hiding the login form behind an error screen is the worse failure of the two: it
 * removes the only action available to the person who needs it.
 */
export async function requireGuest(context: { search?: unknown }): Promise<void> {
  const search = (context.search ?? {}) as Record<string, unknown>;

  let session: SessionSnapshot;
  try {
    session = await bootstrapSession('quiet');
  } catch {
    // The probe failed. We do not know whether there is a session, so we do not
    // redirect — and we do not take the page down either.
    return;
  }

  if (session.status === 'authenticated') {
    throw redirect({ href: safeRedirectPath(search['redirect'], '/overview') });
  }
}